import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../axios";
import "./CustomizationAdvanced.scss";
import layouts from "../layouts/layouts";
import { useSelector } from "react-redux";
import { useAuth } from "../../../context/AuthContext";
import { FabricRenderer } from "../layouts/fabric";
import { getMaterialTypeForLayout, recordDownload } from "../../../utils/downloadHistory";
import { readMirrorSetting, saveMirrorSetting } from "../../../utils/mirrorSetting";
import Settings from "./Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, CircularProgress } from "@mui/material";
import { confirmAction, notifyInfo, notifyError, getApiErrorMessage, promptText } from "../../../utils/notify";
import { parseTextDSL } from "../layouts/textDSL";
import { isEmptyLayoutChanges } from "../layouts/fabric/layoutChanges";
import { useMaterialCustomization, saveCustomizationOnce } from "../customizations/materialCustomization";
import { rebaseTextOverrides } from "../customizations/textMerge";
import MirrorToggle from "../customizations/MirrorToggle";
import { getVariantKeyFromParams, useResolvedVariant, createPattern, updatePattern } from "../customizations/patterns";

const EMPTY_OVERRIDES = {};
const MIN_DOWNLOAD_SPINNER_MS = 650;
const waitForNextPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve)),
  );
const waitForMinimumSpinner = (startedAt) =>
  new Promise((resolve) =>
    setTimeout(
      resolve,
      Math.max(0, MIN_DOWNLOAD_SPINNER_MS - (Date.now() - startedAt)),
    ),
  );

const CustomizationAdvanced = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedLayoutType = params.get("selectedLayoutType");
  // Wzór: z aplikacji ('builtin:0') albo zakładu ('pattern:15')
  const variantKey = getVariantKeyFromParams(params);
  const resolvedVariant = useResolvedVariant(variantKey);
  const selectedTemplate = params.get("selectedTemplate");
  const navigate = useNavigate();
  const generatingUrlFor = (key) => `/personForm?openGeneratingPanel=true&selectedLayoutType=${selectedLayoutType}&variant=${encodeURIComponent(key)}&selectedTemplate=${encodeURIComponent(selectedTemplate || "")}`;
  const returnToGeneratingUrl = generatingUrlFor(variantKey);
  const person = useSelector((state) => state.person.person);

  const { activeSubscription, can_edit_layout, can_manage_patterns } = useAuth();
  const [isSavingPattern, setIsSavingPattern] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [buttonDisabledPDF, setButtonDisabledPDF] = useState(false);
  const [buttonDisabledJPG, setButtonDisabledJPG] = useState(false);
  const [isMirrorImage, setIsMirrorImage] = useState(() => readMirrorSetting(selectedLayoutType));
  const settingsRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [canvasInstance, setCanvasInstance] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [pdfAlert, setPdfAlert] = useState(null);
  const [historyState, setHistoryState] = useState({
    canUndo: false,
    canRedo: false,
  });
  const isDownloading = buttonDisabledPDF || buttonDisabledJPG;
  const rendererRef = useRef(null);
  const historyRef = useRef([]);
  const redoRef = useRef([]);
  const isRestoringHistoryRef = useRef(false);
  const token = localStorage.getItem("token");

  // Poprawki zapisane w bazie dla tego zmarłego i materiału (wspólne dla kont zakładu)
  const savedState = useMaterialCustomization({
    formDataId: person?.formDataId,
    layoutType: selectedLayoutType,
    variantKey,
  });
  // Stan startowy edytora - ustalany raz po wczytaniu,
  // żeby kolejne zapisy nie przerysowywały canvasu w trakcie edycji
  const [initialSaved, setInitialSaved] = useState(null);
  const textOverridesRef = useRef({});
  const textBaseRef = useRef({});

  // Layout JSON wzoru z aplikacji, na którym bazuje wybrany wzór (czekamy na wczytanie wzoru zakładu)
  const currentLayout =
    selectedLayoutType && resolvedVariant.loaded
      ? layouts[selectedLayoutType]?.params[resolvedVariant.baseVariant]?.layout
      : null;
  const currentPattern = resolvedVariant.pattern;
  const patternChanges = currentPattern?.layoutChanges || null;

  useEffect(() => {
    if (!savedState.loaded || !currentLayout || initialSaved) return;
    const saved = savedState.customization;
    // Zmiany w formularzu po poprawce nakładamy na poprawiony tekst (textMerge.js)
    const { overrides, base } = rebaseTextOverrides({
      layout: currentLayout,
      data: person,
      textOverrides: saved?.textOverrides,
      textBase: saved?.textBase,
    });
    textOverridesRef.current = overrides;
    textBaseRef.current = base;
    setInitialSaved({
      overrides: Object.keys(overrides).length > 0 ? { ...overrides } : EMPTY_OVERRIDES,
      layoutChanges: saved?.layoutChanges || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedState.loaded, currentLayout]);

  const canUpdatePattern = can_manage_patterns && currentPattern && !currentPattern.deleted;
  const materialName = layouts[selectedLayoutType]?.name || "Materiał";
  const variantName = currentPattern?.name
    || layouts[selectedLayoutType]?.params[resolvedVariant.baseVariant]?.typeName
    || "Standardowy";

  // Cały aktualny układ (wzór + poprawki) zapisany jako nowy wzór zakładu
  const handleSaveAsPattern = async () => {
    const name = await promptText({
      title: "Zapisz jako nowy wzór",
      placeholder: "np. Nasz nekrolog",
      confirmButtonText: "Zapisz wzór",
    });
    if (!name) return;

    setIsSavingPattern(true);
    try {
      const layoutChanges = rendererRef.current?.getPatternChanges?.();
      const pattern = await createPattern({
        layoutType: selectedLayoutType,
        baseVariant: resolvedVariant.baseVariant,
        name,
        layoutChanges: isEmptyLayoutChanges(layoutChanges) ? null : layoutChanges,
      });
      await savedState.flush();
      // Poprawki tekstu tego zmarłego przechodzą na nowy wzór (układ jest już w samym wzorze)
      if (person?.formDataId && Object.keys(textOverridesRef.current).length > 0) {
        await saveCustomizationOnce(person.formDataId, selectedLayoutType, pattern.key, {
          textOverrides: { ...textOverridesRef.current },
          textBase: { ...textBaseRef.current },
        }).catch((error) => console.error("Nie udało się przenieść poprawek tekstu:", error));
      }
      navigate(generatingUrlFor(pattern.key));
    } catch (error) {
      notifyError(getApiErrorMessage(error, "Nie udało się zapisać wzoru."));
    } finally {
      setIsSavingPattern(false);
    }
  };

  // Aktualny układ zapisany w wybranym wzorze zakładu
  const handleUpdatePattern = async () => {
    const confirmed = await confirmAction({
      title: `Zaktualizować wzór „${currentPattern?.name}”?`,
      text: "Nowy układ będzie używany we wszystkich kolejnych materiałach na tym wzorze.",
      confirmButtonText: "Tak, zaktualizuj",
    });
    if (!confirmed) return;

    setIsSavingPattern(true);
    try {
      const layoutChanges = rendererRef.current?.getPatternChanges?.();
      await updatePattern(currentPattern.id, {
        layoutChanges: isEmptyLayoutChanges(layoutChanges) ? null : layoutChanges,
      });
      // Układ jest już we wzorze - poprawki układu tego zmarłego nie są potrzebne
      savedState.scheduleSave({ layoutChanges: null });
      await savedState.flush();
      navigate(returnToGeneratingUrl);
    } catch (error) {
      notifyError(getApiErrorMessage(error, "Nie udało się zaktualizować wzoru."));
    } finally {
      setIsSavingPattern(false);
    }
  };

  const defaultTextColor = currentLayout?.theme?.defaultTextColor || "#000";

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!activeSubscription || !can_edit_layout) {
      navigate("/");
      window.scrollTo(0, 0);
    }
  }, [activeSubscription, can_edit_layout, navigate]);

  // Bez danych zmarłego (np. link otwarty w nowej karcie) podgląd byłby pusty
  useEffect(() => {
    if (token && !person?.surname) {
      notifyInfo("Uzupełnij najpierw dane zmarłego w formularzu.");
      navigate("/personForm", { replace: true });
    }
  }, [token, person, navigate]);

  useEffect(() => {
    if (!pdfAlert) return undefined;

    const timeoutId = setTimeout(() => setPdfAlert(null), 4500);

    return () => clearTimeout(timeoutId);
  }, [pdfAlert]);

  const updateHistoryState = () => {
    setHistoryState({
      canUndo: historyRef.current.length > 1,
      canRedo: redoRef.current.length > 0,
    });
  };

  const serializeCanvas = () => {
    const canvas = rendererRef.current?.getCanvas?.();
    if (!canvas) return null;

    return JSON.stringify(canvas.toJSON(["elementId", "elementLabel"]));
  };

  // Zapis zmian układu (i tekstów zmienionych bezpośrednio na podglądzie) w bazie
  const saveLayout = () => {
    const result = rendererRef.current?.getLayoutChanges?.();
    if (!result) return;

    const changedTextIds = Object.keys(result.changedTexts);
    changedTextIds.forEach((elementId) => {
      const element = currentLayout?.elements?.find((item) => item.id === elementId);
      textOverridesRef.current[elementId] = result.changedTexts[elementId];
      textBaseRef.current[elementId] = parseTextDSL(element?.content || "", person);
    });

    savedState.scheduleSave({
      layoutChanges: isEmptyLayoutChanges(result.layoutChanges) ? null : result.layoutChanges,
      ...(changedTextIds.length > 0
        ? { textOverrides: { ...textOverridesRef.current }, textBase: { ...textBaseRef.current } }
        : {}),
    });
  };

  const pushHistorySnapshot = () => {
    if (isRestoringHistoryRef.current) return;

    const snapshot = serializeCanvas();
    if (!snapshot) return;

    const lastSnapshot = historyRef.current[historyRef.current.length - 1];
    if (lastSnapshot === snapshot) return;

    // Pierwszy wpis to stan po wczytaniu - nie ma czego zapisywać
    const isInitialSnapshot = historyRef.current.length === 0;
    historyRef.current = [...historyRef.current, snapshot].slice(-40);
    redoRef.current = [];
    updateHistoryState();
    if (!isInitialSnapshot) saveLayout();
  };

  const restoreSnapshot = async (snapshot) => {
    const canvas = rendererRef.current?.getCanvas?.();
    if (!canvas || !snapshot) return;

    isRestoringHistoryRef.current = true;
    canvas.discardActiveObject();
    await canvas.loadFromJSON(JSON.parse(snapshot));
    canvas.renderAll();
    setSelectedObject(null);
    isRestoringHistoryRef.current = false;
  };

  const handleUndo = async () => {
    if (historyRef.current.length <= 1) return;

    const currentSnapshot = historyRef.current.pop();
    redoRef.current = [currentSnapshot, ...redoRef.current];
    await restoreSnapshot(historyRef.current[historyRef.current.length - 1]);
    updateHistoryState();
    saveLayout();
  };

  const handleRedo = async () => {
    const nextSnapshot = redoRef.current.shift();
    if (!nextSnapshot) return;

    historyRef.current = [...historyRef.current, nextSnapshot];
    await restoreSnapshot(nextSnapshot);
    updateHistoryState();
    saveLayout();
  };

  // Zmiany są zapisywane w tle - przed wyjściem wysyłamy tylko zaległe
  const handleBack = async () => {
    await savedState.flush();
    navigate(returnToGeneratingUrl);
  };

  const handleResetLayout = async () => {
    const confirmed = await confirmAction({
      title: "Zresetować układ?",
      text: "Wszystkie ręczne zmiany układu zostaną cofnięte.",
      confirmButtonText: "Tak, resetuj",
    });
    if (!confirmed) return;

    rendererRef.current?.resetLayout?.();
    historyRef.current = [];
    redoRef.current = [];
    setSelectedObject(null);
    savedState.scheduleSave({ layoutChanges: null });
    window.requestAnimationFrame(() => {
      pushHistorySnapshot();
    });
  };

  async function incrementDownload(fileFormat) {
    try {
      await recordDownload(getMaterialTypeForLayout(selectedLayoutType, currentLayout?.id), fileFormat);
    } catch (error) {
      console.log("Nie udało się zapisać historii pobrania.");
    }
  }
  async function handleDownloadPDF() {
    if (!rendererRef.current) return;
    const startedAt = Date.now();
    let nextAlert = null;

    setButtonDisabledPDF(true);
    setPdfAlert(null);

    try {
      await waitForNextPaint();
      const filename = `${person.surname}_${layouts[selectedLayoutType]?.name || "dokument"}.pdf`;
      await rendererRef.current.exportPDF(filename);
      await incrementDownload('PDF');
      nextAlert = { severity: "success", message: "PDF został pobrany." };
    } catch (error) {
      console.error("Błąd przy generowaniu PDF:", error);
      nextAlert = {
        severity: "error",
        message: "Nie udało się pobrać PDF. Spróbuj ponownie.",
      };
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledPDF(false);
      setPdfAlert(nextAlert);
    }
  }

  async function handleDownloadJPG() {
    if (!rendererRef.current) return;
    const startedAt = Date.now();
    let nextAlert = null;

    setButtonDisabledJPG(true);
    setPdfAlert(null);

    try {
      await waitForNextPaint();
      const filename = `${person.surname}_${layouts[selectedLayoutType]?.name || "dokument"}.jpg`;
      await rendererRef.current.exportJPG(filename);
      await incrementDownload('JPG');
      nextAlert = { severity: "success", message: "JPG został pobrany." };
    } catch (error) {
      console.error("Błąd przy generowaniu JPG:", error);
      nextAlert = {
        severity: "error",
        message: "Nie udało się pobrać JPG. Spróbuj ponownie.",
      };
    } finally {
      await waitForMinimumSpinner(startedAt);
      setButtonDisabledJPG(false);
      setPdfAlert(nextAlert);
    }
  }

  const handleMirrorImageChange = (event) => {
    setIsMirrorImage(event.target.checked);
    saveMirrorSetting(selectedLayoutType, event.target.checked);
  };

  const handleElementSelect = (elementId, fabricObj) => {
    setSelectedObject(fabricObj || null);

    // Telefon: narzędzia są pod przyklejonym podglądem - przewijamy do nich, jeśli są poza ekranem
    if (isMobile && fabricObj) {
      window.requestAnimationFrame(() => {
        const panel = settingsRef.current;
        if (panel && panel.getBoundingClientRect().top > window.innerHeight * 0.7) {
          panel.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  };

  // Wybór elementu z listy w panelu (małe napisy trudno trafić palcem)
  const handlePickObject = (fabricObj) => {
    const canvas = rendererRef.current?.getCanvas?.();
    if (!canvas) return;
    if (fabricObj) {
      canvas.setActiveObject(fabricObj);
    } else {
      canvas.discardActiveObject();
    }
    canvas.requestRenderAll();
    setSelectedObject(fabricObj || null);
  };

  const handleElementChange = () => {
    pushHistorySnapshot();
  };

  const templateUrl = selectedTemplate
    ? `${axios.defaults.imgURL}/${selectedTemplate}?token=${token}`
    : null;

  useEffect(() => {
    let frameId;

    const syncCanvasInstance = () => {
      const nextCanvas = rendererRef.current?.getCanvas?.() || null;
      setCanvasInstance((prevCanvas) =>
        prevCanvas === nextCanvas ? prevCanvas : nextCanvas,
      );

      if (!nextCanvas) {
        frameId = window.requestAnimationFrame(syncCanvasInstance);
      }
    };

    syncCanvasInstance();

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [currentLayout, templateUrl]);

  useEffect(() => {
    if (!canvasInstance) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      historyRef.current = [];
      redoRef.current = [];
      pushHistorySnapshot();
    });

    return () => window.cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasInstance, currentLayout, templateUrl]);

  const downloadDisabled =
    isLoading || !selectedLayoutType || selectedTemplate === null || isDownloading;
  const canvasSize = currentLayout?.canvas || { width: 1000, height: 707 };

  return (
    <section className="customizationAdvanced_section">
      {/* Górny pasek: kontekst, pobieranie, odbicie lustrzane i zapis wzoru - jak w „Edytuj tekst” */}
      <div className="customizationAdvanced_topBar">
        <button
          className="btnDownload btnBackTop app-btn-outline"
          onClick={handleBack}
          disabled={isDownloading}
        >
          <ArrowBackIcon fontSize="small" />
          Wróć
        </button>
        <div className="customizationAdvanced_context">
          <span className="customizationAdvanced_kicker">Edytuj układ</span>
          <h1>
            {materialName}
            <span className="customizationAdvanced_variant"> · wzór {variantName}</span>
          </h1>
        </div>
        <div className="customizationAdvanced_actions">
          <MirrorToggle
            className="customizationMirror"
            checked={isMirrorImage}
            onChange={handleMirrorImageChange}
            disabled={isDownloading}
          />
          <button className="btnDownload" onClick={handleDownloadPDF} disabled={downloadDisabled}>
            {buttonDisabledPDF ? <CircularProgress size={18} color="inherit" /> : null}
            Pobierz PDF
          </button>
          <button className="btnDownload" onClick={handleDownloadJPG} disabled={downloadDisabled}>
            {buttonDisabledJPG ? <CircularProgress size={18} color="inherit" /> : null}
            Pobierz JPG
          </button>
        </div>
        {can_manage_patterns ? (
          <div className="customizationAdvanced_patternActions">
            {canUpdatePattern ? (
              <button
                className="btnDownload app-btn-outline"
                onClick={handleUpdatePattern}
                disabled={isDownloading || isSavingPattern || !initialSaved}
              >
                Zaktualizuj wzór „{currentPattern.name}”
              </button>
            ) : null}
            <button
              className="btnDownload app-btn-outline"
              onClick={handleSaveAsPattern}
              disabled={isDownloading || isSavingPattern || !initialSaved}
            >
              Zapisz jako nowy wzór
            </button>
          </div>
        ) : null}
        {pdfAlert && (
          <Alert
            severity={pdfAlert.severity}
            onClose={() => setPdfAlert(null)}
            className="downloadStatusAlert"
          >
            {pdfAlert.message}
          </Alert>
        )}
      </div>

      <div className="customizationAdvanced_workspace">
        <div className="customizationAdvanced_previewArea">
          {/* Okno w proporcjach materiału - bez pustego miejsca pod podglądem */}
          <div
            className="canvas-wrapper"
            style={{ aspectRatio: `${canvasSize.width} / ${canvasSize.height}` }}
          >
            {currentLayout && templateUrl && initialSaved ? (
              <FabricRenderer
                key={`${selectedLayoutType}-${variantKey}-${selectedTemplate}`}
                ref={rendererRef}
                layout={currentLayout}
                data={person}
                templateUrl={templateUrl}
                editable={true}
                overrides={initialSaved.overrides}
                patternChanges={patternChanges}
                layoutChanges={initialSaved.layoutChanges}
                isMirrorImage={isMirrorImage}
                onElementSelect={handleElementSelect}
                onElementChange={handleElementChange}
              />
            ) : (
              <div className="customizationAdvanced_loading" role="status">
                <CircularProgress size={28} />
              </div>
            )}
          </div>
        </div>

        {currentLayout && (
          <Settings
            ref={settingsRef}
            canvas={canvasInstance}
            selectedObject={selectedObject}
            defaultTextColor={defaultTextColor}
            onHistoryCommit={pushHistorySnapshot}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onResetLayout={handleResetLayout}
            onPickObject={handlePickObject}
            canUndo={historyState.canUndo}
            canRedo={historyState.canRedo}
            expandSelection={(fabricObject, start, end) => (
              rendererRef.current?.expandSelectionToFields?.(fabricObject, start, end) || { start, end }
            )}
          />
        )}
      </div>
    </section>
  );
};

export default CustomizationAdvanced;
